"use client";

import { cn } from "@/lib/utils";
import {
    IAccountAssets,
    IBlocks,
    ITheme,
    IThroneWishlist,
    IUserData,
} from "@/types";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import React from "react";
import ThroneWishlistCustomization from "./tabs/throneWishlist/customization";
import BlockToggle from "./tabs/toggle";
import BlockMetadata from "./tabs/metadata";
import ThroneWishlistOptions from "./tabs/throneWishlist/options";
interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    assets: IAccountAssets[];
    theme: ITheme;
    newBlock?: string | null;
    impersonate?: IUserData | null;
}
export default function BlockThroneWishlist({
    admin,
    user,
    block,
    update,
    style,
    assets,
    theme,
    newBlock,
    impersonate,
}: Props) {
    let storeButtonStyle = {
        ...style,
        background: style.background,
        color: style.color,
    };
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [items, setItems] = React.useState<IThroneWishlist["items"]>([]);
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await ThroneWishlist(
                    blockUpdate.options.username,
                    blockUpdate.options.user_id
                );
                setItems(data.wishlist?.items || []);
            } catch (error) {
                console.error(error);
                setItems([]);
            }
        };
        fetchData();
    }, [user, blockUpdate.options.user_id]);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Throne Wishlist",
            description:
                "Add wishlist items from your Throne page to your website.",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <ThroneWishlistOptions
                        key={block.id}
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <ThroneWishlistCustomization
                        block={blockUpdate}
                        update={handleUpdate}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    const getGridCols = (itemCount: number) => {
        switch (itemCount) {
            case 1:
                return "grid-cols-3 [&>*:first-child]:col-span-3";
            case 2:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:last-child]:col-span-1";
            case 3:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*]:col-span-1 [&>*:last-child]:col-span-full";
            case 4:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:last-child]:col-span-2";
            case 5:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:nth-child(3)]:col-span-1 [&>*:nth-child(4)]:col-span-1 [&>*:last-child]:col-span-1";
            case 6:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:nth-child(3)]:col-span-1 [&>*:nth-child(4)]:col-span-2 [&>*:nth-child(5)]:col-span-2 [&>*:last-child]:col-span-1";
            case 7:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2";
            case 8:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(4)]:col-span-2 [&>*:nth-child(5)]:col-span-2 [&>*:nth-child(8)]:col-span-2";
            case 9:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2 [&>*:nth-child(8)]:col-span-2";
            case 10:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2 ";
            default:
                return "grid-cols-3 [&>*:nth-child(3n+1)]:col-span-1 [&>*:nth-child(3n+2)]:col-span-1 [&>*:nth-child(3n)]:col-span-1";
        }
    };
    const productBackground = blockUpdate.options?.custom_theme
        ? blockUpdate.options?.product_background
        : user?.theme.button;
    const productText = blockUpdate.options?.custom_theme
        ? blockUpdate.options?.product_text
        : user?.theme.text_col_button;
    if (blockUpdate.options?.custom_theme) {
        storeButtonStyle.background =
            blockUpdate.options?.store_button_background;
        storeButtonStyle.color = blockUpdate.options?.store_button_text;
    }
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Thone Wishlist"}
            isOpen={isExpanded}
            onClose={handleClose}
            key={block.id}
        >
            <div
                key={block.id}
                className={`flex w-full flex-row flex-wrap gap-2 p-2 ${user.theme.button_rounded} relative`}
                id="FourthwallProducts"
            >
                <div
                    className={cn(
                        "grid w-full grid-cols-1 gap-1",
                        getGridCols(items.length)
                    )}
                >
                    {items.map((item) => (
                        <a
                            href={`${item.link}?utm_source=doras-profile&utm_medium=profile-click`}
                            target="_blank"
                            // rel=""
                            key={item.id}
                            className={cn(
                                "relative flex scale-95 flex-col items-center gap-0 transition-transform duration-300 hover:scale-100",
                                user?.theme.button_rounded === "rounded-full"
                                    ? "rounded-md"
                                    : user?.theme.button_rounded
                            )}
                        >
                            <img
                                src={item.imgLink}
                                alt={item.name}
                                className={cn(
                                    "h-64 w-full overflow-hidden object-cover",
                                    user?.theme.button_rounded ===
                                        "rounded-full"
                                        ? "rounded-md"
                                        : user?.theme.button_rounded
                                )}
                            />
                            <div
                                className={cn(
                                    "absolute inset-x-0 bottom-0 flex w-full flex-col items-start gap-2 px-1",
                                    user?.theme.button_rounded ===
                                        "rounded-full"
                                        ? "rounded-md"
                                        : user?.theme.button_rounded,
                                    "rounded-t-none"
                                )}
                                style={{
                                    background: productBackground,
                                    color: productText,
                                }}
                            >
                                <h3 className="w-full truncate text-ellipsis text-lg font-semibold">
                                    {item.name}
                                </h3>
                            </div>
                        </a>
                    ))}
                </div>
                <a
                    className={`w-full`}
                    href={"https://throne.com/" + blockUpdate.options.username}
                    target="_blank"
                >
                    <button
                        id="store-button"
                        className={`{formatClassName(text)} 
                
                ${user?.theme?.button_rounded}
                w-full 
                px-1 
                py-3 text-xl
                 transition-all 
                duration-150 hover:scale-105
                
                `}
                        style={storeButtonStyle}
                    >
                        <span
                            id="block-text"
                            className={`block w-full px-6 text-center font-bold`}
                        >
                            See More
                        </span>
                    </button>
                </a>
            </div>
        </AdminPopover>
    );
}

async function ThroneWishlist(
    username: string,
    user_id: string
): Promise<{
    wishlist: IThroneWishlist | null;
}> {
    const wishlistResponse: IThroneWishlist = await fetch(
        "https://us-central1-onlywish-9d17b.cloudfunctions.net/api-extensionv2/v1/twitch/wishlist/" +
            user_id,
        {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        }
    ).then(async (response) => await response.json());
    const items = wishlistResponse.items.map((item) => {
        item.link = `https://throne.com/${username}/item/${item.id}`;
        return item;
    });
    return {
        wishlist: {
            collections: wishlistResponse.collections,
            featuredItem: wishlistResponse.featuredItem && {
                ...wishlistResponse.featuredItem,
                link: `https://throne.com/${username}/item/${wishlistResponse.featuredItem.id}`,
            },
            items,
        },
    };
}
